import React, { useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import {
  FaDownload,
  FaPrint,
  FaRedo,
  FaPlus,
  FaTrash,
  FaBookOpen,
  FaUndoAlt,
  FaExclamationTriangle,
  FaBookmark,
} from 'react-icons/fa';
import SEO from '../components/SEO';
import LogoUploader from '../components/LogoUploader';
import { drawPdfLogo } from '../utils/logoUpload';

const RECEIPT_TYPES = [
  { v: 'issue', l: 'Book Issue', Icon: FaBookOpen, accent: '#0e7490' },
  { v: 'return', l: 'Book Return', Icon: FaUndoAlt, accent: '#15803d' },
  { v: 'reservation', l: 'Reservation', Icon: FaBookmark, accent: '#7c3aed' },
  { v: 'fine', l: 'Fine / Penalty', Icon: FaExclamationTriangle, accent: '#b91c1c' },
];

const TEMPLATES = [
  { id: 'classic', label: 'Library Classic A4', accent: '#0e7490', format: 'a4' },
  { id: 'modern', label: 'Modern Sidebar', accent: '#7c3aed', format: 'a4' },
  { id: 'minimal', label: 'Minimal Mono', accent: '#111827', format: 'a4' },
  { id: 'banner', label: 'Bold Banner', accent: '#b45309', format: 'a4' },
  { id: 'thermal', label: 'Quick Slip 80mm', accent: '#15803d', format: [80, 297] },
];

const PAYMENT_MODES = ['Cash', 'Card', 'UPI', 'Wallet', 'Net Banking', 'Cheque', 'N/A'];

const hexToRgbArr = (hex) => {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
};

const pad2 = (n) => String(n).padStart(2, '0');
const toDMY = (d) => `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
const todayDMY = () => toDMY(new Date());
const addDaysDMY = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toDMY(d);
};
const nowHM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const genReceiptNo = (prefix) => {
  const year = new Date().getFullYear();
  const seq = Math.floor(1000 + Math.random() * 9000);
  return `${prefix}-${year}-${seq}`;
};

const formatINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const PREFIX_MAP = { issue: 'ISS', return: 'RET', reservation: 'RES', fine: 'FIN' };

const newBook = (title = '', author = '', isbn = '', accession = '', callNo = '', issueDate = todayDMY(), dueDate = addDaysDMY(14), returnDate = '', overdueDays = 0, fine = 0) => ({
  id: Math.random().toString(36).slice(2, 9),
  title,
  author,
  isbn,
  accession,
  callNo,
  issueDate,
  dueDate,
  returnDate,
  overdueDays,
  fine,
});

const LibraryBookReceiptGenerator = () => {
  const [receiptType, setReceiptType] = useState('issue');
  const [template, setTemplate] = useState('classic');

  const [library, setLibrary] = useState({
    name: 'Central University Library',
    address: 'Block B, University Campus, Pune 411007',
    phone: '+91 20 2569 1111',
    email: 'library@centraluniv.edu.in',
    code: 'CUL-2026',
    branch: 'Main Branch',
  });

  const [member, setMember] = useState({
    name: 'Aarav Sharma',
    memberId: 'STU-2024-0457',
    category: 'Student', // Student / Faculty / Staff / Public
    classDept: 'B.Tech CSE - 3rd Year',
    phone: '+91 90000 22233',
    email: 'aarav.sharma@centraluniv.edu.in',
    validUntil: addDaysDMY(365),
  });

  const [meta, setMeta] = useState({
    receiptNo: genReceiptNo('ISS'),
    date: todayDMY(),
    time: nowHM(),
    librarian: 'Mrs. Kavita Rao',
    paymentMode: 'N/A',
  });

  const [books, setBooks] = useState([
    newBook('Introduction to Algorithms', 'Cormen, Leiserson, Rivest & Stein', '978-0262033848', 'ACC-10245', 'CS.004.CORM', todayDMY(), addDaysDMY(14)),
    newBook('Clean Code', 'Robert C. Martin', '978-0132350884', 'ACC-10876', 'CS.005.MART', todayDMY(), addDaysDMY(14)),
  ]);

  const [charges, setCharges] = useState({
    lateFeePerDay: 5,
    lostBookFee: 0,
    damageFee: 0,
    securityDeposit: 0,
    membershipFee: 0,
    otherCharge: 0,
    discount: 0,
    roundOff: true,
  });

  const [notes, setNotes] = useState(
    'Please return books on or before the due date to avoid late fees. Books damaged or lost must be replaced or paid for as per library policy.'
  );

  const [logo, setLogo] = useState(null);

  const accent = TEMPLATES.find((t) => t.id === template)?.accent || '#0e7490';
  const typeConfig = RECEIPT_TYPES.find((t) => t.v === receiptType);

  // ---- Switch receipt type → swap defaults ----
  const switchType = (type) => {
    setReceiptType(type);
    setMeta((m) => ({ ...m, receiptNo: genReceiptNo(PREFIX_MAP[type] || 'LIB') }));
    if (type === 'issue') {
      setCharges((c) => ({ ...c, lateFeePerDay: 5, lostBookFee: 0, damageFee: 0, membershipFee: 0, otherCharge: 0 }));
      setBooks([
        newBook('Introduction to Algorithms', 'Cormen et al.', '978-0262033848', 'ACC-10245', 'CS.004.CORM', todayDMY(), addDaysDMY(14)),
      ]);
    } else if (type === 'return') {
      setBooks([
        newBook('Clean Code', 'Robert C. Martin', '978-0132350884', 'ACC-10876', 'CS.005.MART', addDaysDMY(-20), addDaysDMY(-6), todayDMY(), 6, 30),
      ]);
    } else if (type === 'reservation') {
      setBooks([
        newBook('Deep Learning', 'Ian Goodfellow', '978-0262035613', 'ACC-12001', 'CS.006.GOOD', todayDMY(), addDaysDMY(7)),
      ]);
    } else if (type === 'fine') {
      setBooks([
        newBook('The Pragmatic Programmer', 'Andrew Hunt', '978-0201616224', 'ACC-09988', 'CS.007.HUNT', addDaysDMY(-30), addDaysDMY(-16), todayDMY(), 16, 80),
      ]);
      setCharges((c) => ({ ...c, lateFeePerDay: 5, otherCharge: 0 }));
    }
  };

  // Auto-calc overdue & fine for each book
  const computedBooks = useMemo(() => {
    return books.map((b) => {
      const lateFee = Number(charges.lateFeePerDay) || 0;
      const days = Number(b.overdueDays) || 0;
      const autoFine = days * lateFee;
      const explicitFine = Number(b.fine) || 0;
      return { ...b, lineFine: Math.max(autoFine, explicitFine) };
    });
  }, [books, charges.lateFeePerDay]);

  // ---------- COMPUTATIONS ----------
  const totals = useMemo(() => {
    const bookFines = computedBooks.reduce((s, b) => s + (Number(b.lineFine) || 0), 0);
    const lost = Number(charges.lostBookFee) || 0;
    const damage = Number(charges.damageFee) || 0;
    const sec = Number(charges.securityDeposit) || 0;
    const mem = Number(charges.membershipFee) || 0;
    const other = Number(charges.otherCharge) || 0;
    const disc = Number(charges.discount) || 0;
    const subtotal = bookFines + lost + damage + sec + mem + other;
    const afterDisc = Math.max(0, subtotal - disc);
    const grandRounded = Math.round(afterDisc);
    return {
      bookFines,
      lost,
      damage,
      sec,
      mem,
      other,
      disc,
      subtotal,
      grand: Math.round(afterDisc * 100) / 100,
      grandRounded,
      roundOff: grandRounded - afterDisc,
    };
  }, [computedBooks, charges]);

  const finalAmount = charges.roundOff ? totals.grandRounded : totals.grand;

  const updateBook = (id, patch) =>
    setBooks((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const addBook = () => setBooks((prev) => [...prev, newBook()]);
  const removeBook = (id) => setBooks((prev) => prev.filter((b) => b.id !== id));

  const handleReset = () => {
    setLibrary({ name: '', address: '', phone: '', email: '', code: '', branch: '' });
    setMember({ name: '', memberId: '', category: 'Student', classDept: '', phone: '', email: '', validUntil: '' });
    setMeta({
      receiptNo: genReceiptNo(PREFIX_MAP[receiptType] || 'LIB'),
      date: todayDMY(),
      time: nowHM(),
      librarian: '',
      paymentMode: 'N/A',
    });
    setBooks([newBook()]);
    setCharges({ lateFeePerDay: 5, lostBookFee: 0, damageFee: 0, securityDeposit: 0, membershipFee: 0, otherCharge: 0, discount: 0, roundOff: true });
  };

  const typeLabel = typeConfig?.l || 'Library Receipt';
  const headerTitle = {
    issue: 'BOOK ISSUE RECEIPT',
    return: 'BOOK RETURN RECEIPT',
    reservation: 'BOOK RESERVATION SLIP',
    fine: 'LIBRARY FINE RECEIPT',
  }[receiptType] || 'LIBRARY RECEIPT';

  // ---------- PDF HELPERS ----------
  const drawBooksTable = (doc, startY, left, right, color) => {
    let y = startY;
    doc.setFillColor(...color);
    doc.rect(left, y, right - left, 9, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.text('#', left + 3, y + 6);
    doc.text('Title / Author', left + 10, y + 6);
    doc.text('Accession', left + 88, y + 6);
    doc.text('Issue', left + 118, y + 6);
    doc.text('Due', left + 138, y + 6);
    if (receiptType === 'return' || receiptType === 'fine') {
      doc.text('Ret', left + 156, y + 6);
      doc.text('Od', right - 18, y + 6, { align: 'right' });
    }
    doc.text('Fine', right - 3, y + 6, { align: 'right' });
    y += 9;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    computedBooks.forEach((b, idx) => {
      const titleLines = doc.splitTextToSize(b.title || '-', 70);
      const authorLines = doc.splitTextToSize(b.author ? `by ${b.author}` : '', 70);
      const rowH = Math.max(8, (titleLines.length + authorLines.length) * 3.6 + 2);
      doc.rect(left, y, right - left, rowH);
      doc.text(String(idx + 1), left + 3, y + 5);
      doc.text(titleLines, left + 10, y + 5);
      if (b.author) {
        doc.setTextColor(100, 116, 139);
        doc.setFontSize(7.5);
        doc.text(authorLines, left + 10, y + 5 + titleLines.length * 3.6);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8.5);
      }
      doc.text(b.accession || '-', left + 88, y + 5);
      doc.text(b.issueDate || '-', left + 118, y + 5);
      doc.text(b.dueDate || '-', left + 138, y + 5);
      if (receiptType === 'return' || receiptType === 'fine') {
        doc.text(b.returnDate || '-', left + 156, y + 5);
        doc.text(String(b.overdueDays || 0), right - 18, y + 5, { align: 'right' });
      }
      doc.text(b.lineFine ? formatINR(b.lineFine) : '-', right - 3, y + 5, { align: 'right' });
      y += rowH;
    });
    return y;
  };

  const drawTotalsBlock = (doc, startY, left, right, color) => {
    let y = startY + 4;
    const totalsX = right - 90;
    const row = (label, value, bold = false, big = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(big ? 13 : 10);
      doc.text(label, totalsX, y);
      doc.text(value, right, y, { align: 'right' });
      y += big ? 8 : 5;
    };
    if (totals.bookFines > 0) row('Late Fee (books)', formatINR(totals.bookFines));
    if (totals.lost > 0) row('Lost Book Fee', formatINR(totals.lost));
    if (totals.damage > 0) row('Damage Fee', formatINR(totals.damage));
    if (totals.sec > 0) row('Security Deposit', formatINR(totals.sec));
    if (totals.mem > 0) row('Membership Fee', formatINR(totals.mem));
    if (totals.other > 0) row('Other Charges', formatINR(totals.other));
    row('Subtotal', formatINR(totals.subtotal));
    if (totals.disc > 0) row('Discount / Waiver', `- ${formatINR(totals.disc)}`);
    if (charges.roundOff && Math.abs(totals.roundOff) > 0.001)
      row('Round Off', totals.roundOff >= 0 ? `+ ${formatINR(totals.roundOff)}` : `- ${formatINR(Math.abs(totals.roundOff))}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.line(totalsX, y, right, y);
    y += 6;
    doc.setTextColor(...color);
    row(finalAmount > 0 ? 'AMOUNT PAYABLE' : 'NO PAYMENT DUE', `Rs. ${formatINR(finalAmount)}`, true, true);
    doc.setTextColor(0, 0, 0);
    if (finalAmount > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Payment Mode: ${meta.paymentMode}`, totalsX, y);
      y += 5;
    }
    return y;
  };

  const drawNotes = (doc, startY, left, right, color) => {
    let y = startY + 4;
    if (!notes) return y;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...color);
    doc.text('Notes', left, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);
    const lines = doc.splitTextToSize(notes, right - left);
    lines.forEach((ln) => { doc.text(ln, left, y); y += 4; });
    doc.setTextColor(0, 0, 0);
    return y;
  };

  // 1. CLASSIC A4
  const renderClassic = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);

    let y = 16;
    if (logo) drawPdfLogo(doc, logo, left, 10, 22, library.name, [240, 240, 240]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.setTextColor(...color);
    doc.text(library.name || 'Library', logo ? left + 26 : left, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    if (library.address) {
      const lines = doc.splitTextToSize(library.address, 110);
      lines.forEach((ln) => { doc.text(ln, logo ? left + 26 : left, y); y += 4; });
    }
    const sub = [library.phone, library.email].filter(Boolean).join('  ·  ');
    if (sub) { doc.text(sub, logo ? left + 26 : left, y); y += 4; }
    if (library.code || library.branch) {
      doc.text([library.code && `Code: ${library.code}`, library.branch].filter(Boolean).join('  ·  '), logo ? left + 26 : left, y);
      y += 4;
    }

    y += 2;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.5);
    doc.line(left, y, right, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...color);
    doc.text(headerTitle, left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(`No: ${meta.receiptNo}`, right, y - 5, { align: 'right' });
    doc.text(`${meta.date}  ${meta.time}`, right, y, { align: 'right' });
    y += 8;

    // Member block
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Member Details', left, y);
    doc.text('Receipt Info', (left + right) / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(member.name || '—', left, y);
    doc.text(`Librarian: ${meta.librarian || '—'}`, (left + right) / 2, y);
    y += 5;
    const mInfo = [member.memberId && `ID: ${member.memberId}`, member.category, member.classDept].filter(Boolean).join('  |  ');
    if (mInfo) doc.text(mInfo, left, y);
    if (member.validUntil) doc.text(`Member Valid Until: ${member.validUntil}`, (left + right) / 2, y);
    y += 5;
    if (member.phone || member.email) {
      doc.text([member.phone, member.email].filter(Boolean).join('  |  '), left, y);
      y += 5;
    }

    y += 4;
    y = drawBooksTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotes(doc, y, left, right, color);

    // Signatures
    y = Math.min(y + 12, 270);
    doc.setDrawColor(120, 120, 120);
    doc.line(left, y, left + 60, y);
    doc.line(right - 60, y, right, y);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Member Signature', left + 30, y + 5, { align: 'center' });
    doc.text('Librarian Signature', right - 30, y + 5, { align: 'center' });

    return doc;
  };

  // 2. MODERN SIDEBAR
  const renderModern = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210, h = 297;
    const sideW = 60;
    const color = hexToRgbArr(accent);

    doc.setFillColor(...color);
    doc.rect(0, 0, sideW, h, 'F');
    if (logo) drawPdfLogo(doc, logo, (sideW - 30) / 2, 14, 30, library.name, [255, 255, 255]);

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    const titleY = logo ? 50 : 22;
    const nameLines = doc.splitTextToSize(library.name || 'Library', sideW - 10);
    nameLines.forEach((ln, i) => doc.text(ln, sideW / 2, titleY + i * 6, { align: 'center' }));
    let sy = titleY + nameLines.length * 6 + 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (library.address) {
      const lines = doc.splitTextToSize(library.address, sideW - 10);
      lines.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; });
    }
    if (library.phone) { doc.text(library.phone, sideW / 2, sy, { align: 'center' }); sy += 3.5; }
    if (library.email) {
      const eLines = doc.splitTextToSize(library.email, sideW - 10);
      eLines.forEach((ln) => { doc.text(ln, sideW / 2, sy, { align: 'center' }); sy += 3.5; });
    }
    sy += 3;
    doc.setDrawColor(255, 255, 255);
    doc.line(8, sy, sideW - 8, sy);
    sy += 5;
    if (library.code) { doc.setFont('helvetica', 'bold'); doc.text('LIBRARY CODE', sideW / 2, sy, { align: 'center' }); sy += 3.5; doc.setFont('helvetica', 'normal'); doc.text(library.code, sideW / 2, sy, { align: 'center' }); sy += 5; }
    if (library.branch) { doc.setFont('helvetica', 'bold'); doc.text('BRANCH', sideW / 2, sy, { align: 'center' }); sy += 3.5; doc.setFont('helvetica', 'normal'); doc.text(library.branch, sideW / 2, sy, { align: 'center' }); sy += 5; }

    // Right pane
    const left = sideW + 10;
    const right = w - 14;
    let y = 18;
    doc.setTextColor(...color);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(headerTitle, left, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`#${meta.receiptNo}   ·   ${meta.date} ${meta.time}`, left, y);
    y += 8;

    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Member', left, y);
    doc.text('Librarian', (left + right) / 2, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.text(member.name || '—', left, y);
    doc.text(meta.librarian || '—', (left + right) / 2, y);
    y += 5;
    const mInfo = [member.memberId && `ID: ${member.memberId}`, member.category].filter(Boolean).join('  |  ');
    if (mInfo) doc.text(mInfo, left, y);
    if (member.validUntil) doc.text(`Valid: ${member.validUntil}`, (left + right) / 2, y);
    y += 5;
    if (member.classDept) { doc.text(member.classDept, left, y); y += 5; }
    if (member.phone || member.email) {
      doc.text([member.phone, member.email].filter(Boolean).join('  ·  '), left, y);
      y += 5;
    }

    y += 4;
    y = drawBooksTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotes(doc, y, left, right, color);

    return doc;
  };

  // 3. MINIMAL
  const renderMinimal = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = [40, 40, 40];

    let y = 18;
    if (logo) drawPdfLogo(doc, logo, left, 12, 18, library.name, [240, 240, 240]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text(library.name || 'Library', logo ? left + 22 : left, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(80, 80, 80);
    const headLine = [library.address, library.phone, library.email].filter(Boolean).join('  ·  ');
    if (headLine) {
      const lines = doc.splitTextToSize(headLine, right - (logo ? left + 22 : left));
      lines.forEach((ln) => { doc.text(ln, logo ? left + 22 : left, y); y += 4; });
    }
    if (library.code) { doc.text(`Code: ${library.code}${library.branch ? '  ·  ' + library.branch : ''}`, logo ? left + 22 : left, y); y += 4; }

    y += 4;
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.6);
    doc.line(left, y, right, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(0, 0, 0);
    doc.text(headerTitle, left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(meta.receiptNo, right, y, { align: 'right' });
    y += 6;
    doc.setFontSize(9);
    doc.text(`${meta.date}  ${meta.time}  ·  Librarian: ${meta.librarian || '—'}`, left, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Member:', left, y);
    doc.setFont('helvetica', 'normal');
    doc.text(`${member.name || '—'}${member.memberId ? `  (${member.memberId})` : ''}`, left + 18, y);
    y += 5;
    const mInfo2 = [member.category, member.classDept, member.phone].filter(Boolean).join('  ·  ');
    if (mInfo2) { doc.text(mInfo2, left, y); y += 5; }
    if (member.validUntil) { doc.text(`Membership valid until: ${member.validUntil}`, left, y); y += 5; }
    y += 2;

    y = drawBooksTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotes(doc, y, left, right, color);
    return doc;
  };

  // 4. BANNER
  const renderBanner = () => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const w = 210;
    const left = 14;
    const right = w - 14;
    const color = hexToRgbArr(accent);

    doc.setFillColor(...color);
    doc.rect(0, 0, w, 40, 'F');
    if (logo) drawPdfLogo(doc, logo, left, 6, 28, library.name, [255, 255, 255]);
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(22);
    doc.text(library.name || 'Library', logo ? left + 32 : left, 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(headerTitle, logo ? left + 32 : left, 26);
    doc.setFontSize(9);
    const sub = [library.address, library.phone].filter(Boolean).join('  ·  ');
    if (sub) {
      const lines = doc.splitTextToSize(sub, w - (logo ? left + 32 : left) - 14);
      lines.forEach((ln, i) => doc.text(ln, logo ? left + 32 : left, 32 + i * 4));
    }
    doc.text(`#${meta.receiptNo}`, right, 14, { align: 'right' });
    doc.text(meta.date, right, 20, { align: 'right' });
    doc.text(meta.time, right, 26, { align: 'right' });
    if (library.code) doc.text(library.code, right, 32, { align: 'right' });

    let y = 50;
    doc.setTextColor(...color);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Member', left, y);
    doc.text('Librarian', (left + right) / 2, y);
    y += 5;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(member.name || '—', left, y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(meta.librarian || '—', (left + right) / 2, y);
    y += 5;
    doc.setFontSize(9);
    const mInfo = [member.memberId && `ID: ${member.memberId}`, member.category, member.classDept].filter(Boolean).join('  |  ');
    if (mInfo) doc.text(mInfo, left, y);
    if (member.validUntil) doc.text(`Valid: ${member.validUntil}`, (left + right) / 2, y);
    y += 5;
    if (member.phone || member.email) { doc.text([member.phone, member.email].filter(Boolean).join('  ·  '), left, y); y += 5; }

    y += 3;
    y = drawBooksTable(doc, y, left, right, color);
    y = drawTotalsBlock(doc, y, left, right, color);
    y = drawNotes(doc, y, left, right, color);
    return doc;
  };

  // 5. THERMAL 80mm
  const renderThermal = () => {
    const w = 80, h = 297;
    const doc = new jsPDF({ unit: 'mm', format: [w, h] });
    const color = hexToRgbArr(accent);
    let y = 8;
    if (logo) {
      drawPdfLogo(doc, logo, (w - 16) / 2, 4, 16, library.name, [240, 240, 240]);
      y = 22;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...color);
    doc.text(library.name || 'Library', w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(0, 0, 0);
    if (library.address) {
      const lines = doc.splitTextToSize(library.address, w - 10);
      lines.forEach((ln) => { doc.text(ln, w / 2, y, { align: 'center' }); y += 3; });
    }
    if (library.phone) { doc.text(library.phone, w / 2, y, { align: 'center' }); y += 3; }
    if (library.code) { doc.text(library.code, w / 2, y, { align: 'center' }); y += 3; }
    y += 1;
    doc.setDrawColor(0, 0, 0);
    doc.line(5, y, w - 5, y);
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(headerTitle, w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`#${meta.receiptNo}`, 5, y);
    doc.text(meta.date, w - 5, y, { align: 'right' });
    y += 4;
    doc.text(`Member: ${member.name || '-'}`, 5, y); y += 4;
    if (member.memberId) { doc.text(`ID: ${member.memberId}`, 5, y); y += 4; }
    if (meta.librarian) { doc.text(`By: ${meta.librarian}`, 5, y); y += 4; }
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 4;

    // Books
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text('Book', 5, y);
    doc.text('Due', 48, y);
    doc.text('Fine', w - 5, y, { align: 'right' });
    y += 2;
    doc.line(5, y, w - 5, y);
    y += 3;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    computedBooks.forEach((b) => {
      const titleLines = doc.splitTextToSize(b.title || '-', 40);
      doc.text(titleLines, 5, y);
      doc.text(b.dueDate || '-', 48, y);
      doc.text(b.lineFine ? formatINR(b.lineFine) : '-', w - 5, y, { align: 'right' });
      y += Math.max(4, titleLines.length * 3.5);
      if (b.accession) {
        doc.setTextColor(100, 116, 139);
        doc.setFontSize(7);
        doc.text(`Acc: ${b.accession}${b.callNo ? '  ' + b.callNo : ''}`, 5, y);
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(7.5);
        y += 3;
      }
    });
    y += 1;
    doc.line(5, y, w - 5, y);
    y += 4;

    // Totals
    const row = (label, value, bold = false) => {
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(bold ? 11 : 8);
      doc.text(label, 5, y);
      doc.text(value, w - 5, y, { align: 'right' });
      y += bold ? 6 : 4;
    };
    if (totals.bookFines > 0) row('Late Fee', formatINR(totals.bookFines));
    if (totals.lost > 0) row('Lost Book', formatINR(totals.lost));
    if (totals.damage > 0) row('Damage', formatINR(totals.damage));
    if (totals.sec > 0) row('Security', formatINR(totals.sec));
    if (totals.mem > 0) row('Membership', formatINR(totals.mem));
    if (totals.other > 0) row('Other', formatINR(totals.other));
    if (totals.disc > 0) row('Discount', `- ${formatINR(totals.disc)}`);
    y += 1;
    doc.setDrawColor(...color);
    doc.setLineWidth(0.4);
    doc.line(5, y, w - 5, y);
    y += 5;
    doc.setTextColor(...color);
    row(finalAmount > 0 ? 'PAYABLE' : 'NO DUES', `Rs. ${formatINR(finalAmount)}`, true);
    doc.setTextColor(0, 0, 0);
    if (finalAmount > 0) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Paid via ${meta.paymentMode}`, w / 2, y, { align: 'center' });
      y += 5;
    }
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('Please return books on time.', w / 2, y, { align: 'center' });
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...color);
    doc.text('Happy Reading!', w / 2, y, { align: 'center' });
    return doc;
  };

  const TEMPLATE_RENDERERS = {
    classic: renderClassic,
    modern: renderModern,
    minimal: renderMinimal,
    banner: renderBanner,
    thermal: renderThermal,
  };

  const buildPDF = () => (TEMPLATE_RENDERERS[template] || renderClassic)();

  const handleDownloadPDF = () => {
    const doc = buildPDF();
    doc.save(`Library_${typeLabel.replace(/\s+/g, '_')}_${meta.receiptNo || 'receipt'}.pdf`);
  };
  const handlePrint = () => {
    const doc = buildPDF();
    doc.autoPrint();
    const url = doc.output('bloburl');
    window.open(url, '_blank');
  };

  const isReturnOrFine = receiptType === 'return' || receiptType === 'fine';

  return (
    <div className="calculator-page fade-in">
      <SEO
        title="Library Book Receipt Generator (Issue, Return, Fine, Reservation)"
        description="Free Library Book Receipt Generator - create Issue, Return, Reservation and Fine receipts with member details, book accession & call numbers, due dates, late-fee auto calculation and 5 printable templates. Download as PDF."
        keywords="library receipt generator, book issue slip, book return receipt, library fine receipt, library reservation slip, accession number, college library receipt, school library bill"
        canonicalPath="/library-book-receipt"
        calculatorType="LibraryBookReceiptGenerator"
        calculatorDescription="Generate library receipts for book issue, return, reservation and fines with member info, accession/call numbers, auto-calculated overdue fines and 5 printable templates."
      />

      <div className="calculator-header">
        <h1>Library Book Receipt Generator</h1>
        <p>Issue, Return, Reservation & Fine receipts with auto-calculated late fee — 5 printable templates</p>
      </div>

      <div className="calculator-container">
        <div className="calculator-inputs">
          {/* Receipt Type */}
          <div className="input-group">
            <label>Receipt Type</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
              {RECEIPT_TYPES.map((opt) => {
                const active = receiptType === opt.v;
                const Ic = opt.Icon;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    className={`btn ${active ? 'btn-primary' : 'btn-outline'}`}
                    style={{ padding: '8px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    onClick={() => switchType(opt.v)}
                  >
                    <Ic /> {opt.l}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Template */}
          <div className="input-group">
            <label>Receipt Template</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              {TEMPLATES.map((t) => {
                const active = template === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setTemplate(t.id)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: `1.5px solid ${active ? t.accent : '#cbd5e1'}`,
                      background: active ? t.accent : '#fff',
                      color: active ? '#fff' : '#0f172a',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: t.accent, border: active ? '1px solid #fff' : 'none' }} />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <LogoUploader logo={logo} setLogo={setLogo} label="Library Logo" accent={accent} />

          <h3 style={{ marginTop: 12, marginBottom: 12 }}>Library Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Library Name</label>
              <input type="text" value={library.name} onChange={(e) => setLibrary({ ...library, name: e.target.value })} placeholder="e.g. Central University Library" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input type="text" value={library.phone} onChange={(e) => setLibrary({ ...library, phone: e.target.value })} placeholder="+91 ..." />
            </div>
          </div>
          <div className="input-group">
            <label>Address</label>
            <input type="text" value={library.address} onChange={(e) => setLibrary({ ...library, address: e.target.value })} placeholder="Street, City, PIN" />
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Email</label>
              <input type="text" value={library.email} onChange={(e) => setLibrary({ ...library, email: e.target.value })} placeholder="library@..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Library Code</label>
              <input type="text" value={library.code} onChange={(e) => setLibrary({ ...library, code: e.target.value })} placeholder="e.g. CUL-2026" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Branch</label>
              <input type="text" value={library.branch} onChange={(e) => setLibrary({ ...library, branch: e.target.value })} placeholder="Main / Annex" />
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Receipt Info</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Receipt No</label>
              <input type="text" value={meta.receiptNo} onChange={(e) => setMeta({ ...meta, receiptNo: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Date</label>
              <input type="text" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} placeholder="DD/MM/YYYY" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Time</label>
              <input type="text" value={meta.time} onChange={(e) => setMeta({ ...meta, time: e.target.value })} placeholder="HH:MM" />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Librarian / Staff Name</label>
              <input type="text" value={meta.librarian} onChange={(e) => setMeta({ ...meta, librarian: e.target.value })} placeholder="Mrs./Mr." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Payment Mode</label>
              <select value={meta.paymentMode} onChange={(e) => setMeta({ ...meta, paymentMode: e.target.value })}>
                {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Member Details</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Member Name</label>
              <input type="text" value={member.name} onChange={(e) => setMember({ ...member, name: e.target.value })} placeholder="Full name" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Member ID</label>
              <input type="text" value={member.memberId} onChange={(e) => setMember({ ...member, memberId: e.target.value })} placeholder="STU-..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Category</label>
              <select value={member.category} onChange={(e) => setMember({ ...member, category: e.target.value })}>
                <option value="Student">Student</option>
                <option value="Faculty">Faculty</option>
                <option value="Staff">Staff</option>
                <option value="Research Scholar">Research Scholar</option>
                <option value="Public">Public</option>
              </select>
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 2 }}>
              <label>Class / Department</label>
              <input type="text" value={member.classDept} onChange={(e) => setMember({ ...member, classDept: e.target.value })} placeholder="e.g. B.Tech CSE - 3rd Year" />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Phone</label>
              <input type="text" value={member.phone} onChange={(e) => setMember({ ...member, phone: e.target.value })} placeholder="+91 ..." />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Membership Valid Until</label>
              <input type="text" value={member.validUntil} onChange={(e) => setMember({ ...member, validUntil: e.target.value })} placeholder="DD/MM/YYYY" />
            </div>
          </div>
          <div className="input-group">
            <label>Email</label>
            <input type="text" value={member.email} onChange={(e) => setMember({ ...member, email: e.target.value })} placeholder="member@..." />
          </div>

          <h3 style={{ marginTop: 16, marginBottom: 8 }}>Books</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {books.map((b, idx) => (
              <div key={b.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 10, background: '#f8fafc' }}>
                <div className="flex-row" style={{ alignItems: 'flex-end' }}>
                  <div className="input-group" style={{ flex: 3, marginBottom: 0 }}>
                    <label>{idx + 1}. Book Title</label>
                    <input type="text" value={b.title} onChange={(e) => updateBook(b.id, { title: e.target.value })} placeholder="e.g. Introduction to Algorithms" />
                  </div>
                  <div className="input-group" style={{ flex: 2, marginBottom: 0 }}>
                    <label>Author(s)</label>
                    <input type="text" value={b.author} onChange={(e) => updateBook(b.id, { author: e.target.value })} placeholder="e.g. Cormen et al." />
                  </div>
                  <button type="button" onClick={() => removeBook(b.id)} className="btn btn-outline" style={{ padding: '8px 10px', marginBottom: 0 }} title="Remove">
                    <FaTrash />
                  </button>
                </div>
                <div className="flex-row" style={{ marginTop: 8 }}>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>ISBN</label>
                    <input type="text" value={b.isbn} onChange={(e) => updateBook(b.id, { isbn: e.target.value })} placeholder="978-..." />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Accession No</label>
                    <input type="text" value={b.accession} onChange={(e) => updateBook(b.id, { accession: e.target.value })} placeholder="ACC-..." />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Call No / Class No</label>
                    <input type="text" value={b.callNo} onChange={(e) => updateBook(b.id, { callNo: e.target.value })} placeholder="e.g. CS.004.CORM" />
                  </div>
                </div>
                <div className="flex-row" style={{ marginTop: 8 }}>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Issue Date</label>
                    <input type="text" value={b.issueDate} onChange={(e) => updateBook(b.id, { issueDate: e.target.value })} placeholder="DD/MM/YYYY" />
                  </div>
                  <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                    <label>Due Date</label>
                    <input type="text" value={b.dueDate} onChange={(e) => updateBook(b.id, { dueDate: e.target.value })} placeholder="DD/MM/YYYY" />
                  </div>
                  {isReturnOrFine && (
                    <>
                      <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Return Date</label>
                        <input type="text" value={b.returnDate} onChange={(e) => updateBook(b.id, { returnDate: e.target.value })} placeholder="DD/MM/YYYY" />
                      </div>
                      <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Overdue Days</label>
                        <input type="number" min="0" value={b.overdueDays} onChange={(e) => updateBook(b.id, { overdueDays: e.target.value })} />
                      </div>
                      <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                        <label>Manual Fine ₹</label>
                        <input type="number" min="0" step="0.01" value={b.fine} onChange={(e) => updateBook(b.id, { fine: e.target.value })} />
                      </div>
                    </>
                  )}
                </div>
                {isReturnOrFine && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#475569', textAlign: 'right' }}>
                    Computed Fine:{' '}
                    <strong>
                      ₹ {formatINR(
                        Math.max(
                          (Number(b.overdueDays) || 0) * (Number(charges.lateFeePerDay) || 0),
                          Number(b.fine) || 0
                        )
                      )}
                    </strong>{' '}
                    <span style={{ color: '#94a3b8' }}>(max of auto vs manual)</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <button type="button" onClick={addBook} className="btn btn-outline" style={{ marginTop: 10 }}>
            <FaPlus /> Add Book
          </button>

          <h3 style={{ marginTop: 16, marginBottom: 12 }}>Charges & Fees</h3>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Late Fee / Day (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.lateFeePerDay} onChange={(e) => setCharges({ ...charges, lateFeePerDay: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Lost Book Fee (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.lostBookFee} onChange={(e) => setCharges({ ...charges, lostBookFee: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Damage Fee (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.damageFee} onChange={(e) => setCharges({ ...charges, damageFee: e.target.value })} />
            </div>
          </div>
          <div className="flex-row">
            <div className="input-group" style={{ flex: 1 }}>
              <label>Security Deposit (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.securityDeposit} onChange={(e) => setCharges({ ...charges, securityDeposit: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Membership Fee (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.membershipFee} onChange={(e) => setCharges({ ...charges, membershipFee: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Other Charges (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.otherCharge} onChange={(e) => setCharges({ ...charges, otherCharge: e.target.value })} />
            </div>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Discount / Waiver (₹)</label>
              <input type="number" min="0" step="0.01" value={charges.discount} onChange={(e) => setCharges({ ...charges, discount: e.target.value })} />
            </div>
          </div>
          <div className="input-group">
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={charges.roundOff} onChange={(e) => setCharges({ ...charges, roundOff: e.target.checked })} />
              Round off payable to nearest rupee
            </label>
            <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
              Per-book fine = max(Overdue Days × Late Fee/Day, Manual Fine). Books without overdue or manual fine contribute ₹0.
            </div>
          </div>

          <div className="input-group">
            <label>Notes (optional)</label>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Library policies, due-date reminder, etc."
              style={{ width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 }}
            />
          </div>

          <div className="button-group" style={{ marginTop: 16 }}>
            <button type="button" onClick={handleDownloadPDF} className="btn btn-primary">
              <FaDownload /> Download PDF
            </button>
            <button type="button" onClick={handlePrint} className="btn btn-outline">
              <FaPrint /> Print
            </button>
            <button type="button" onClick={handleReset} className="btn btn-outline">
              <FaRedo /> Reset
            </button>
          </div>
        </div>

        <div className="calculator-results">
          <div style={{ marginBottom: 12, fontSize: 13, color: '#475569' }}>
            Live preview · Template: <strong>{TEMPLATES.find((t) => t.id === template)?.label}</strong>
          </div>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(15,23,42,0.08)', overflow: 'hidden' }}>
            <ReceiptPreview
              template={template}
              accent={accent}
              library={library}
              member={member}
              meta={meta}
              books={computedBooks}
              totals={totals}
              charges={charges}
              notes={notes}
              finalAmount={finalAmount}
              logo={logo}
              headerTitle={headerTitle}
              receiptType={receiptType}
            />
          </div>
          <div style={{ marginTop: 12, padding: 12, background: '#ecfeff', border: '1px solid #a5f3fc', borderRadius: 8, fontSize: 13, color: '#155e75' }}>
            <strong>{finalAmount > 0 ? 'Amount Payable' : 'No Dues'}:</strong> ₹ {formatINR(finalAmount)}
            {totals.bookFines > 0 && <> · Late Fee: ₹ {formatINR(totals.bookFines)}</>}
            {totals.disc > 0 && <> · Waiver: ₹ {formatINR(totals.disc)}</>}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- LIVE PREVIEW ----------
const previewBase = {
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  color: '#0f172a',
};

const LogoImg = ({ logo, style }) =>
  logo ? <img src={logo} alt="logo" style={{ objectFit: 'contain', ...style }} /> : null;

const ReceiptPreview = ({ template, accent, library, member, meta, books, totals, charges, notes, finalAmount, logo, headerTitle, receiptType }) => {
  const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const isRetFine = receiptType === 'return' || receiptType === 'fine';

  const BooksTable = ({ compact = false }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? 11 : 12 }}>
      <thead>
        <tr style={{ background: '#f1f5f9' }}>
          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Title / Author</th>
          {!compact && <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Accession</th>}
          <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Due</th>
          {!compact && isRetFine && <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Ret</th>}
          {!compact && isRetFine && <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Od</th>}
          <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 700 }}>Fine</th>
        </tr>
      </thead>
      <tbody>
        {books.map((b) => (
          <tr key={b.id} style={{ borderBottom: '1px dashed #e2e8f0' }}>
            <td style={{ padding: '6px 8px' }}>
              <div>{b.title || '—'}</div>
              {b.author && <div style={{ fontSize: 10, color: '#64748b' }}>by {b.author}</div>}
              {b.callNo && <div style={{ fontSize: 10, color: '#94a3b8' }}>{b.callNo}{b.isbn ? `  ·  ISBN ${b.isbn}` : ''}</div>}
            </td>
            {!compact && <td style={{ padding: '6px 8px', fontSize: 11 }}>{b.accession || '—'}</td>}
            <td style={{ padding: '6px 8px', fontSize: 11 }}>{b.dueDate || '—'}</td>
            {!compact && isRetFine && <td style={{ padding: '6px 8px', fontSize: 11 }}>{b.returnDate || '—'}</td>}
            {!compact && isRetFine && <td style={{ padding: '6px 8px', textAlign: 'right' }}>{b.overdueDays || 0}</td>}
            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{b.lineFine ? fmt(b.lineFine) : '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const Row = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );

  const TotalsBlock = () => (
    <div style={{ marginLeft: 'auto', marginTop: 12, minWidth: 260, fontSize: 13 }}>
      {totals.bookFines > 0 && <Row label="Late Fee (books)" value={fmt(totals.bookFines)} />}
      {totals.lost > 0 && <Row label="Lost Book Fee" value={fmt(totals.lost)} />}
      {totals.damage > 0 && <Row label="Damage Fee" value={fmt(totals.damage)} />}
      {totals.sec > 0 && <Row label="Security Deposit" value={fmt(totals.sec)} />}
      {totals.mem > 0 && <Row label="Membership Fee" value={fmt(totals.mem)} />}
      {totals.other > 0 && <Row label="Other Charges" value={fmt(totals.other)} />}
      <Row label="Subtotal" value={fmt(totals.subtotal)} />
      {totals.disc > 0 && <Row label="Discount / Waiver" value={`- ${fmt(totals.disc)}`} />}
      {charges.roundOff && Math.abs(totals.roundOff) > 0.001 && (
        <Row label="Round Off" value={totals.roundOff >= 0 ? `+ ${fmt(totals.roundOff)}` : `- ${fmt(Math.abs(totals.roundOff))}`} />
      )}
      <div style={{ borderTop: `2px solid ${accent}`, marginTop: 6, paddingTop: 6 }}>
        <Row
          label={<span style={{ color: accent, fontWeight: 800 }}>{finalAmount > 0 ? 'AMOUNT PAYABLE' : 'NO PAYMENT DUE'}</span>}
          value={<span style={{ color: accent, fontWeight: 800 }}>₹ {fmt(finalAmount)}</span>}
        />
      </div>
      {finalAmount > 0 && (
        <div style={{ fontSize: 12, color: '#64748b', textAlign: 'right', marginTop: 4 }}>
          Payment Mode: <strong>{meta.paymentMode}</strong>
        </div>
      )}
    </div>
  );

  const NotesBlock = () =>
    notes ? (
      <div style={{ marginTop: 14 }}>
        <div style={{ fontWeight: 700, color: accent, marginBottom: 4 }}>Notes</div>
        <div style={{ fontSize: 12, color: '#334155', whiteSpace: 'pre-wrap' }}>{notes}</div>
      </div>
    ) : null;

  // 1. CLASSIC
  if (template === 'classic') {
    return (
      <div style={{ ...previewBase, padding: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {logo && <LogoImg logo={logo} style={{ height: 56, width: 56 }} />}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 18, color: accent }}>{library.name || 'Library'}</div>
            <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5, marginTop: 2 }}>
              {library.address}
              {(library.phone || library.email) && <><br />{[library.phone, library.email].filter(Boolean).join(' · ')}</>}
              {(library.code || library.branch) && <><br />{[library.code && `Code: ${library.code}`, library.branch].filter(Boolean).join('  ·  ')}</>}
            </div>
          </div>
        </div>
        <div style={{ borderTop: `2px solid ${accent}`, margin: '12px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: accent }}>{headerTitle}</div>
          <div style={{ fontSize: 11, color: '#475569', textAlign: 'right' }}>
            No: <strong>{meta.receiptNo}</strong><br />
            {meta.date} {meta.time}
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 10 }}>
          <div>
            <div style={{ fontWeight: 700, color: accent, fontSize: 12 }}>Member</div>
            <div style={{ fontWeight: 700, fontSize: 13 }}>{member.name || '—'}</div>
            <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
              {member.memberId && <>ID: {member.memberId}<br /></>}
              {[member.category, member.classDept].filter(Boolean).join('  ·  ')}
              {member.phone && <><br />Tel: {member.phone}</>}
              {member.email && <><br />{member.email}</>}
              {member.validUntil && <><br />Valid Until: {member.validUntil}</>}
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 700, color: accent, fontSize: 12 }}>Receipt Info</div>
            <div style={{ fontSize: 12, lineHeight: 1.6 }}>
              Librarian: <strong>{meta.librarian || '—'}</strong><br />
              Type: {headerTitle}<br />
              Payment Mode: {meta.paymentMode}
            </div>
          </div>
        </div>
        <div style={{ marginTop: 14 }}><BooksTable /></div>
        <TotalsBlock />
        <NotesBlock />
        <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 160, textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
            Member Signature
          </div>
          <div style={{ borderTop: '1px solid #94a3b8', paddingTop: 4, width: 160, textAlign: 'center', fontSize: 11, fontWeight: 700 }}>
            Librarian Signature
          </div>
        </div>
      </div>
    );
  }

  // 2. MODERN SIDEBAR
  if (template === 'modern') {
    return (
      <div style={{ ...previewBase, display: 'flex', minHeight: 500 }}>
        <div style={{ background: accent, color: '#fff', padding: 16, width: 170, flexShrink: 0 }}>
          {logo && (
            <div style={{ background: '#fff', borderRadius: 6, padding: 4, textAlign: 'center', marginBottom: 10 }}>
              <LogoImg logo={logo} style={{ height: 56 }} />
            </div>
          )}
          <div style={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2 }}>{library.name || 'Library'}</div>
          <div style={{ fontSize: 10, opacity: 0.9, marginTop: 6, lineHeight: 1.5 }}>
            {library.address}
            {library.phone && <><br />{library.phone}</>}
            {library.email && <><br />{library.email}</>}
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.4)', margin: '10px 0' }} />
          {library.code && (
            <div style={{ fontSize: 10 }}>
              <div style={{ opacity: 0.85, fontWeight: 700 }}>LIBRARY CODE</div>
              <div>{library.code}</div>
            </div>
          )}
          {library.branch && (
            <div style={{ fontSize: 10, marginTop: 6 }}>
              <div style={{ opacity: 0.85, fontWeight: 700 }}>BRANCH</div>
              <div>{library.branch}</div>
            </div>
          )}
        </div>
        <div style={{ flex: 1, padding: 20 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: accent }}>{headerTitle}</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>#{meta.receiptNo}  ·  {meta.date} {meta.time}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, marginTop: 14 }}>
            <div>
              <div style={{ fontWeight: 700 }}>Member</div>
              <div style={{ fontWeight: 700 }}>{member.name || '—'}</div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {[member.memberId && `ID: ${member.memberId}`, member.category, member.classDept].filter(Boolean).join('  |  ')}
              </div>
              {(member.phone || member.email) && <div style={{ fontSize: 11, color: '#64748b' }}>{[member.phone, member.email].filter(Boolean).join('  ·  ')}</div>}
              {member.validUntil && <div style={{ fontSize: 11, color: '#64748b' }}>Valid: {member.validUntil}</div>}
            </div>
            <div>
              <div><strong>Librarian:</strong> {meta.librarian || '—'}</div>
              <div><strong>Payment:</strong> {meta.paymentMode}</div>
            </div>
          </div>
          <div style={{ marginTop: 14 }}><BooksTable /></div>
          <TotalsBlock />
          <NotesBlock />
        </div>
      </div>
    );
  }

  // 3. MINIMAL
  if (template === 'minimal') {
    return (
      <div style={{ ...previewBase, padding: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {logo && <LogoImg logo={logo} style={{ height: 44, width: 44 }} />}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 16 }}>{library.name || 'Library'}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {[library.address, library.phone, library.email].filter(Boolean).join('  ·  ')}
              {library.code && <> · Code {library.code}</>}
            </div>
          </div>
        </div>
        <div style={{ borderTop: '2px solid #0f172a', margin: '12px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: 1 }}>{headerTitle}</div>
          <div style={{ fontSize: 12 }}>{meta.receiptNo}</div>
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
          {meta.date}  {meta.time}  ·  Librarian: {meta.librarian || '—'}
        </div>
        <div style={{ marginTop: 12, fontSize: 12 }}>
          <strong>Member:</strong> {member.name || '—'}
          {member.memberId && <> ({member.memberId})</>}
          <br />
          <span style={{ color: '#64748b' }}>{[member.category, member.classDept, member.phone].filter(Boolean).join('  ·  ')}</span>
          {member.validUntil && <><br /><span style={{ color: '#64748b' }}>Valid: {member.validUntil}</span></>}
        </div>
        <div style={{ marginTop: 12 }}><BooksTable /></div>
        <TotalsBlock />
        <NotesBlock />
      </div>
    );
  }

  // 4. BANNER
  if (template === 'banner') {
    return (
      <div style={{ ...previewBase }}>
        <div style={{ background: accent, color: '#fff', padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          {logo && (
            <div style={{ background: '#fff', borderRadius: 6, padding: 4 }}>
              <LogoImg logo={logo} style={{ height: 56, width: 56 }} />
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 20 }}>{library.name || 'Library'}</div>
            <div style={{ fontSize: 11, opacity: 0.95 }}>{headerTitle}</div>
            <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>
              {[library.address, library.phone].filter(Boolean).join('  ·  ')}
            </div>
          </div>
          <div style={{ textAlign: 'right', fontSize: 11 }}>
            <div>#{meta.receiptNo}</div>
            <div>{meta.date}</div>
            <div>{meta.time}</div>
            {library.code && <div>{library.code}</div>}
          </div>
        </div>
        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12 }}>
          <div>
            <div style={{ fontWeight: 700, color: accent }}>Member</div>
            <div style={{ fontWeight: 700 }}>{member.name || '—'}</div>
            <div style={{ fontSize: 11, color: '#64748b' }}>
              {[member.memberId && `ID: ${member.memberId}`, member.category, member.classDept].filter(Boolean).join('  |  ')}
            </div>
            {member.validUntil && <div style={{ fontSize: 11, color: '#64748b' }}>Valid: {member.validUntil}</div>}
          </div>
          <div style={{ background: '#fef3c7', padding: 8, borderRadius: 6 }}>
            <div style={{ fontWeight: 700, color: accent, fontSize: 11 }}>Receipt</div>
            <div>Librarian: <strong>{meta.librarian || '—'}</strong></div>
            <div>Payment: <strong>{meta.paymentMode}</strong></div>
          </div>
        </div>
        <div style={{ padding: '0 16px' }}><BooksTable /></div>
        <div style={{ padding: '0 16px 16px' }}>
          <TotalsBlock />
          <NotesBlock />
        </div>
      </div>
    );
  }

  // 5. THERMAL
  return (
    <div style={{ ...previewBase, padding: 14, maxWidth: 320, margin: '12px auto', border: '1px dashed #cbd5e1' }}>
      {logo && (
        <div style={{ textAlign: 'center', marginBottom: 6 }}>
          <LogoImg logo={logo} style={{ height: 46 }} />
        </div>
      )}
      <div style={{ textAlign: 'center', fontWeight: 800, fontSize: 15, color: accent }}>
        {library.name || 'Library'}
      </div>
      <div style={{ textAlign: 'center', fontSize: 11, color: '#475569', marginTop: 2, lineHeight: 1.4 }}>
        {library.address}
        {library.phone && <><br />{library.phone}</>}
        {library.code && <><br />Code: {library.code}</>}
      </div>
      <div style={{ borderTop: '1px dashed #94a3b8', margin: '10px 0' }} />
      <div style={{ textAlign: 'center', fontWeight: 700 }}>{headerTitle}</div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginTop: 6 }}>
        <span>#{meta.receiptNo}</span>
        <span>{meta.date}</span>
      </div>
      <div style={{ fontSize: 11, marginTop: 2 }}>
        Member: <strong>{member.name || '—'}</strong>
        {member.memberId && <> ({member.memberId})</>}
      </div>
      {meta.librarian && <div style={{ fontSize: 11 }}>By: {meta.librarian}</div>}
      <div style={{ marginTop: 8 }}><BooksTable compact /></div>
      <TotalsBlock />
      {finalAmount > 0 && (
        <div style={{ marginTop: 8, fontSize: 11, textAlign: 'center', color: '#64748b' }}>
          Paid via <strong>{meta.paymentMode}</strong>
        </div>
      )}
      <div style={{ marginTop: 8, textAlign: 'center', fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
        Please return books on time.
      </div>
      <div style={{ marginTop: 4, textAlign: 'center', fontWeight: 700, color: accent }}>
        Happy Reading!
      </div>
    </div>
  );
};

export default LibraryBookReceiptGenerator;
